import { cn } from '@exam-prep/ui';
import {
  Activity,
  Bot,
  CalendarCheck,
  CircleUser,
  ClipboardCheck,
  FileQuestionMark,
  Gift,
  GraduationCap,
  Home,
  Library,
  LifeBuoy,
  LogOut,
  Megaphone,
  Monitor,
  Moon,
  Package,
  PanelLeftClose,
  PanelLeftOpen,
  ScrollText,
  Search,
  ShieldCheck,
  Sparkles,
  StickyNote,
  Sun,
  Timer,
  ToggleRight,
  Trophy,
  Users,
  Layers,
  type LucideIcon,
} from 'lucide-react';
import type { NavGroup, NavIcon, NavItem } from '../lib/nav';
import type { ThemeChoice } from '../lib/theme';

const ICONS: Record<NavIcon, LucideIcon> = {
  home: Home,
  today: CalendarCheck,
  review: ClipboardCheck,
  quality: ShieldCheck,
  questions: FileQuestionMark,
  generation: Sparkles,
  kb: Library,
  ask: Search,
  flashcards: StickyNote,
  mocks: Timer,
  grandMock: Trophy,
  packs: Layers,
  students: GraduationCap,
  activity: Activity,
  support: LifeBuoy,
  growth: Megaphone,
  earlyBird: Gift,
  flags: ToggleRight,
  products: Package,
  ai: Bot,
  users: Users,
  audit: ScrollText,
  account: CircleUser,
};

const THEME_ORDER: readonly ThemeChoice[] = ['system', 'dark', 'light'];
const THEME: Record<ThemeChoice, { label: string; Icon: LucideIcon; next: ThemeChoice }> = {
  system: { label: 'System', Icon: Monitor, next: 'dark' },
  dark: { label: 'Dark', Icon: Moon, next: 'light' },
  light: { label: 'Light', Icon: Sun, next: 'system' },
};

const focusRing =
  'outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

export function Brand({ collapsed = false }: { collapsed?: boolean }) {
  return (
    <a
      href="#/"
      className={cn('flex min-w-0 items-center gap-2.5 rounded-lg', focusRing)}
      aria-label="SSCExamPrep Admin home"
    >
      <picture className="shrink-0">
        <source srcSet="/brand/logo-64.webp 1x, /brand/logo-128.webp 2x" type="image/webp" />
        <img src="/brand/logo-192.png" alt="" width={36} height={36} className="size-9" />
      </picture>
      <span className={cn('min-w-0 leading-tight', collapsed && 'sr-only')}>
        <span className="block truncate font-display text-[15px] font-bold">SSCExamPrep</span>
        <span className="block text-[10px] font-semibold tracking-[0.22em] text-muted-foreground uppercase">
          Admin
        </span>
      </span>
    </a>
  );
}

function NavLink({
  item,
  collapsed,
  onNavigate,
}: {
  item: NavItem;
  collapsed: boolean;
  onNavigate?: (() => void) | undefined;
}) {
  const Icon = ICONS[item.icon];
  const badge = item.badge ? (item.badge > 99 ? '99+' : String(item.badge)) : null;
  return (
    <a
      href={item.href}
      aria-current={item.active ? 'page' : undefined}
      title={collapsed ? item.label : undefined}
      onClick={onNavigate}
      data-active={item.active || undefined}
      className={cn(
        'group relative flex min-h-10 items-center gap-3 rounded-lg px-3 text-sm text-muted-foreground transition-colors pointer-coarse:min-h-11',
        'hover:bg-accent hover:text-foreground',
        focusRing,
        collapsed && 'justify-center px-0',
        item.active &&
          'bg-[color-mix(in_srgb,var(--brand-primary)_16%,transparent)] font-semibold text-foreground before:absolute before:inset-y-2 before:-left-3 before:w-1 before:rounded-r-full before:bg-[image:var(--brand-amber-gradient)]',
      )}
    >
      <Icon
        aria-hidden="true"
        className={cn('size-[18px] shrink-0', item.active && 'text-[var(--brand-icon)]')}
        strokeWidth={item.active ? 2.25 : 1.75}
      />
      <span className={cn('min-w-0 flex-1 truncate', collapsed && 'sr-only')}>{item.label}</span>
      {badge ? (
        collapsed ? (
          <span
            className="absolute top-1.5 right-2 size-2 rounded-full bg-[var(--brand-primary)] ring-2 ring-[var(--brand-surface-strong)]"
            aria-hidden="true"
          />
        ) : (
          <span
            className="rounded-full bg-[var(--brand-primary)] px-1.5 py-px text-[11px] leading-4 font-semibold text-white tabular-nums"
            aria-hidden="true"
          >
            {badge}
          </span>
        )
      ) : null}
      {badge ? (
        <span className="sr-only">
          , {badge} {item.badgeLabel ?? 'open'}
        </span>
      ) : null}
    </a>
  );
}

export interface SidebarProps {
  groups: NavGroup[];
  account: NavItem;
  email: string;
  roles: string;
  theme: ThemeChoice;
  onTheme: (t: ThemeChoice) => void;
  onSignOut: () => void;
  collapsed?: boolean | undefined;
  /** Desktop only: collapse/expand to the icon rail. */
  onToggleCollapsed?: (() => void) | undefined;
  /** Drawer only: close after following a link. */
  onNavigate?: (() => void) | undefined;
  navId: string;
}

/** Sidebar body shared by the desktop rail/panel and the mobile drawer. */
export function SidebarContent(p: SidebarProps) {
  const collapsed = p.collapsed ?? false;
  const current = THEME[p.theme];
  const next = THEME[current.next];
  return (
    <>
      <nav aria-label="Admin" className="min-h-0 flex-1 overflow-y-auto px-3 py-2">
        {p.groups.map((g) => (
          <div key={g.id} className="mb-4 last:mb-0">
            <h2
              id={`${p.navId}-${g.id}`}
              className={cn(
                'mb-1 px-3 text-[11px] font-semibold tracking-[0.16em] text-muted-foreground uppercase',
                (collapsed || g.hideLabel) && 'sr-only',
              )}
            >
              {g.label}
            </h2>
            {collapsed && !g.hideLabel ? (
              <hr className="mx-2 mb-2 border-border" aria-hidden="true" />
            ) : null}
            <ul aria-labelledby={`${p.navId}-${g.id}`} className="flex flex-col gap-0.5">
              {g.items.map((i) => (
                <li key={i.id}>
                  <NavLink item={i} collapsed={collapsed} onNavigate={p.onNavigate} />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
      <div className="flex flex-col gap-2 border-t px-3 py-3">
        <NavLink item={p.account} collapsed={collapsed} onNavigate={p.onNavigate} />
        <p
          className={cn('px-3 text-xs leading-snug text-muted-foreground', collapsed && 'sr-only')}
        >
          <span className="block truncate text-foreground" title={p.email}>
            {p.email}
          </span>
          <span className="block truncate">{p.roles}</span>
        </p>
        {collapsed ? (
          <button
            type="button"
            onClick={() => {
              p.onTheme(current.next);
            }}
            title={`Theme: ${current.label}. Switch to ${next.label}`}
            aria-label={`Theme: ${current.label}. Switch to ${next.label}`}
            className={cn(
              'flex min-h-10 items-center justify-center rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground',
              focusRing,
            )}
          >
            <current.Icon aria-hidden="true" className="size-[18px]" />
          </button>
        ) : (
          <div
            role="group"
            aria-label="Theme"
            className="grid grid-cols-3 gap-1 rounded-lg border p-1"
          >
            {THEME_ORDER.map((id) => ({ id, ...THEME[id] })).map((t) => (
              <button
                key={t.id}
                type="button"
                aria-pressed={p.theme === t.id}
                onClick={() => {
                  p.onTheme(t.id);
                }}
                className={cn(
                  'flex min-h-8 items-center justify-center gap-1.5 rounded-md text-xs text-muted-foreground hover:text-foreground pointer-coarse:min-h-11',
                  focusRing,
                  p.theme === t.id && 'bg-accent font-semibold text-foreground',
                )}
              >
                <t.Icon aria-hidden="true" className="size-3.5" />
                {t.label}
              </button>
            ))}
          </div>
        )}
        <div className={cn('flex gap-1', collapsed && 'flex-col')}>
          <button
            type="button"
            onClick={p.onSignOut}
            title={collapsed ? 'Sign out' : undefined}
            className={cn(
              'flex min-h-10 flex-1 items-center gap-3 rounded-lg px-3 text-sm text-muted-foreground hover:bg-accent hover:text-foreground pointer-coarse:min-h-11',
              focusRing,
              collapsed && 'justify-center px-0',
            )}
          >
            <LogOut aria-hidden="true" className="size-[18px] shrink-0" />
            <span className={cn(collapsed && 'sr-only')}>Sign out</span>
          </button>
          {p.onToggleCollapsed ? (
            <button
              type="button"
              onClick={p.onToggleCollapsed}
              aria-expanded={!collapsed}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              className={cn(
                'flex min-h-10 min-w-10 items-center justify-center rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground',
                focusRing,
              )}
            >
              {collapsed ? (
                <PanelLeftOpen aria-hidden="true" className="size-[18px]" />
              ) : (
                <PanelLeftClose aria-hidden="true" className="size-[18px]" />
              )}
            </button>
          ) : null}
        </div>
      </div>
    </>
  );
}
